// src/hooks/useOperationsData.js
//
// Pass 87.2 — THE ONE org-scoped operations data session. A single provider, mounted once at the
// OrganizationDashboardShell level, owns every operational listener (bookings, members, requests)
// so navigation across Overview → Bookings → Team → Clients NEVER re-opens listeners or re-fetches
// the same organisation dataset. Canonical Firestore stays the only authority; this layer is a
// presentation cache, never a second datastore.
//
// Performance model (measured on production sas-niseko: 3010 bookings / ~4.7 MB):
//   • FIRST RENDER is scoped to the current season via `seasonId == currentSeasonId` — provably
//     lossless (every booking stores a seasonId field), ~73 % smaller (827 docs for SAS 26/27).
//   • HISTORICAL seasons + all-time client history load LAZILY on demand (requestAllBookings),
//     one-shot and read-only — current operations never pay for multiple archived seasons.
//   • Auxiliary datasets (sourceIntake / availabilitySlots) subscribe only when a surface that needs
//     them asks (requestAux) — Overview/Team/Clients never open them.
//   • An in-memory, ORG-SCOPED stale-while-revalidate cache paints last-known data instantly on a
//     warm return while the realtime listener reconnects. The cache is keyed by organizationId and
//     is NEVER exposed under a different org (UNKNOWN beats cross-org stale). In-memory only — it
//     dies with the tab/session, so there is no logout/offline-persistence boundary to audit.

import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeBooking, getOrganizationBookings } from '../utils/booking';
import { normalizeBookingCase } from '../utils/bookingCase';
import { normalizeMembershipRecord } from '../utils/organizationData';
import { PLATFORM_COLLECTIONS } from '../utils/platformSchema';
import { detectOperationalConflicts } from '../utils/operationsWorkspace';
import { CURRENT_SEASON_ID } from '../utils/instructorCategory';

const OperationsDataContext = createContext(null);

// Module-level, in-memory, ORG-SCOPED cache for stale-while-revalidate. Keyed by organizationId; a
// value is only ever read back for the SAME org, so a prior org's data can never render under a new
// one. Not persisted (no IndexedDB / localStorage) — no logout/user/tenant boundary to clear.
const OPS_CACHE = new Map();
function readCache(orgId) { return (orgId && OPS_CACHE.get(orgId)) || null; }
function writeCache(orgId, patch) {
  if (!orgId) return;
  OPS_CACHE.set(orgId, { ...(OPS_CACHE.get(orgId) || {}), ...patch });
}

function msOf(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}
const sortByStart = (list) => [...list].sort((a, b) => (msOf(a.startAt) || 0) - (msOf(b.startAt) || 0));

/**
 * OperationsDataProvider — mount ONCE per organisation (the shell keys it by orgId, so an org switch
 * is a clean remount: no previous-org state survives, and the new org seeds from its own cache).
 */
export function OperationsDataProvider({ organizationId = '', currentSeasonId = CURRENT_SEASON_ID, children }) {
  const seed = readCache(organizationId) || {};
  // Current-season realtime bookings (the fast first-render path).
  const [bookings, setBookings] = useState(() => seed.currentBookings || []);
  const [members, setMembers] = useState(() => seed.members || []);
  const [requests, setRequests] = useState(() => seed.requests || []);
  const [bookingsLoading, setBookingsLoading] = useState(() => !(seed.currentBookings));
  const [membersLoading, setMembersLoading] = useState(() => !(seed.members));
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // true while we are revalidating on top of a cached snapshot (warm return) — drives a subtle
  // "Updating…" affordance instead of a blank gate.
  const [fromCache, setFromCache] = useState(() => !!seed.currentBookings);

  // Lazy datasets.
  const [auxRequested, setAuxRequested] = useState(false);
  const [sourceIntakes, setSourceIntakes] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const requestAux = useCallback(() => setAuxRequested(true), []);

  // Lazy all-seasons (historical + all-time client history). One-shot, read-only, cached per org.
  const [allBookings, setAllBookings] = useState(() => seed.allBookings || null);
  const [allBookingsLoading, setAllBookingsLoading] = useState(false);
  const allRequestedRef = useRef(!!seed.allBookings);
  const requestAllBookings = useCallback(() => {
    if (!organizationId || allRequestedRef.current) return;
    allRequestedRef.current = true;
    setAllBookingsLoading(true);
    getOrganizationBookings(db, organizationId)
      .then((list) => {
        const sorted = sortByStart(list || []);
        setAllBookings(sorted);
        writeCache(organizationId, { allBookings: sorted });
      })
      .catch((e) => { console.error('operations all-seasons load error:', e); setAllBookings([]); })
      .then(() => setAllBookingsLoading(false));
  }, [organizationId]);

  // Listener 1 — current-season canonical bookings (scoped; provably lossless, ~73 % smaller).
  useEffect(() => {
    if (!organizationId) { setBookings([]); setBookingsLoading(false); return undefined; }
    const season = currentSeasonId || CURRENT_SEASON_ID;
    const q = query(
      collection(db, PLATFORM_COLLECTIONS.BOOKINGS),
      where('organizationId', '==', organizationId),
      where('seasonId', '==', season),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = sortByStart(snap.docs.map((d) => normalizeBooking({ id: d.id, ...d.data() })));
        setBookings(list);
        setBookingsLoading(false);
        setFromCache(false);
        setLastUpdatedAt(new Date());
        writeCache(organizationId, { currentBookings: list });
      },
      // §13 — surface a scoped-query failure (e.g. a missing composite index) instead of silently
      // falling back to an unbounded full-collection read in production.
      (err) => { console.error('operations current-season bookings listener error:', err); setError(err); setBookingsLoading(false); },
    );
    return () => unsub();
  }, [organizationId, currentSeasonId]);

  // Listener 2 — organisation members (normalised, so CRM tabs and the schedule share one shape).
  useEffect(() => {
    if (!organizationId) { setMembers([]); setMembersLoading(false); return undefined; }
    const q = query(collection(db, PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => normalizeMembershipRecord({ id: d.id, ...d.data() }));
        setMembers(list);
        setMembersLoading(false);
        writeCache(organizationId, { members: list });
      },
      (err) => { console.error('operations members listener error:', err); setError(err); setMembersLoading(false); },
    );
    return () => unsub();
  }, [organizationId]);

  // Listener 3 — booking requests / cases (attention queue). Small; kept live.
  useEffect(() => {
    if (!organizationId) { setRequests([]); return undefined; }
    const q = query(collection(db, PLATFORM_COLLECTIONS.BOOKING_REQUESTS), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => normalizeBookingCase({ id: d.id, ...d.data() }));
        setRequests(list);
        writeCache(organizationId, { requests: list });
      },
      (err) => console.error('operations requests listener error:', err),
    );
    return () => unsub();
  }, [organizationId]);

  // Listener 4+5 — auxiliary datasets. §8: subscribe ONLY once a surface asks (the Bookings
  // workspace), so Overview/Team/Clients never open them.
  useEffect(() => {
    if (!organizationId || !auxRequested) return undefined;
    const qi = query(collection(db, PLATFORM_COLLECTIONS.SOURCE_INTAKE), where('organizationId', '==', organizationId));
    const unsubI = onSnapshot(qi, (snap) => setSourceIntakes(snap.docs.map((d) => ({ intakeId: d.id, ...d.data() }))), (err) => console.error('operations intake listener error:', err));
    const qa = query(collection(db, PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS), where('organizationId', '==', organizationId));
    const unsubA = onSnapshot(qa, (snap) => setAvailabilitySlots(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => console.error('operations availability listener error:', err));
    return () => { unsubI(); unsubA(); };
  }, [organizationId, auxRequested]);

  const conflicts = useMemo(() => detectOperationalConflicts(bookings), [bookings]);

  const value = useMemo(() => ({
    orgId: organizationId,
    currentSeasonId: currentSeasonId || CURRENT_SEASON_ID,
    // current-season fast path
    bookings,
    members,
    bookingRequests: requests,
    conflicts,
    // readiness (granular — never a single global gate)
    bookingsLoading,
    membersLoading,
    loading: bookingsLoading, // the SCHEDULE is usable as soon as current-season bookings arrive
    error,
    lastUpdatedAt,
    fromCache,
    // lazy auxiliary
    sourceIntakes,
    availabilitySlots,
    requestAux,
    // lazy all-seasons (historical + all-time client history)
    allBookings,
    allBookingsLoading,
    requestAllBookings,
  }), [organizationId, currentSeasonId, bookings, members, requests, conflicts, bookingsLoading, membersLoading, error, lastUpdatedAt, fromCache, sourceIntakes, availabilitySlots, requestAux, allBookings, allBookingsLoading, requestAllBookings]);

  return <OperationsDataContext.Provider value={value}>{children}</OperationsDataContext.Provider>;
}

// Returns the provider value, or null when no provider is mounted (standalone/legacy render paths).
export function useOptionalOperationsData() {
  return useContext(OperationsDataContext);
}

export function useOperationsData() {
  const ctx = useContext(OperationsDataContext);
  if (!ctx) throw new Error('useOperationsData must be used within an OperationsDataProvider');
  return ctx;
}

export { OperationsDataContext };
